import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './PfRegistration.css';
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getProjects, getStates, fetchMonths, getMonthlyAttendanceReport } from '../../../services/monthlyAttendanceService';
import { getPfNotRegisterdEmployees } from '../../../services/salaryStructureService';
import { capitalizeFirstLetter } from '../../../utils/validations';

const PfRegistration = () => {
    const [state, setState] = useState(null);
    const [district, setDistrict] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [projectResponse, setProjectResponse] = useState([]);
    const [project, setProject] = useState(null);
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        month: false,
        year: false,
        project: false,
    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);
    const [designationData, setDesignationData] = useState([]);
    const [employeesNotRegistred, setEmployeesNotRegistred] = useState([])
    const [hasFetched, setHasFetched] = useState(false); // New state to track fetch attempt
    const isFetchDataEnabled = state;

    useEffect(() => {
        const fetchStates = async () => {
            try {
                // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/states/nationality/1`);
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        fetchStates();
    }, []);


    useEffect(() => {
        const loadMonths = async () => {
            try {
                const months = await fetchMonths(year);
                setMonthOptions(months);
            } catch (error) {
                console.error("Error fetching months:", error);
                setErrors((prev) => ({ ...prev, general: "Failed to load months" }));
            }
        };
        loadMonths();
    }, [year]);


    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    //const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/districts/state/${state}`);
                    const data = await getDistrict(state);
                    setDistrictOptions(
                        data.map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }))
                    );
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);
    const fetchProjects = async () => {
        //  const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects`);
        const data = await getProjects();
        setProjectResponse(
            data.map(pro => ({
                value: pro.id,
                label: pro.projectCode,
            })));
    }
    useEffect(() => {
        fetchProjects();
    }, [state, district, year]);  // Fetch projects when state, district, or year changes


    // Clear attendance data and fetch status when any dropdown changes
    useEffect(() => {
        setEmployeesNotRegistred([]);
        // setOriginalData([]);
        setEmployeesNotRegistred(false);
    }, [state, district, month, year, project]); // ✅ Add project to depende

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setMonth(null);
                setProject(null); // ✅ Clear project when state changes
                break;
            case 'district':
                setDistrict(value);
                setProject(null); // ✅ Clear project when district changes
                break;
            case 'month':
                setMonth(value);
                break;
            case 'project':
                setProject(value); // ✅ Handle project selection
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setYear(date ? date.getFullYear() : null);
        setErrors(prev => ({ ...prev, year: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';
        //if (!district) newErrors.district = 'District is required';
        //if (!month) newErrors.month = 'Month is required';
        //if (!year) newErrors.year = 'Year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = await getPfNotRegisterdEmployees(state, district);
            setEmployeesNotRegistred(data);
        } catch (error) {
            console.error("Error fetching PF not registered employees:", error);
            alert("Failed to fetch PF report data.");
        }
    };



    const currentYear = new Date().getFullYear();
    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);


    const generateExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("PFRegistration Attendance");

        // Title
        worksheet.mergeCells("A1:AE1");
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "PF Registration Report";
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { vertical: "middle", horizontal: "center" };

        // Headers
        const headers = [
            "S.No",
            "UAN",
            "Prv MemberId",
            "Member Name",
            "Date of Birth",
            "Date of Joining",
            "Gender",
            "Father's Name",
            "Relation",
            "Mobile No",
            "Email",
            "Nationality",
            "EPF Wages",
            "Qualification",
            "Marital Status",
            "Is International Worker",
            "Country of Origin",
            "Passport No",
            "Passport Valid From",
            "Passport Valid Till",
            "Is Physical Handicap",
            "Locomotive",
            "Hearing",
            "Visual",
            "Bank Account No",
            "IFSC Code",
            "Name as per Bank",
            "PAN",
            "Name as per PAN",
            "Aadhar No",
            "Name as per Aadhar"
        ];

        worksheet.addRow(headers);

        // Style header
        const headerRow = worksheet.lastRow;
        headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        headerRow.alignment = { horizontal: "center" };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // Column widths (adjusted)
        worksheet.columns = Array(headers.length).fill({ width: 20 });
        worksheet.getColumn(1).width = 6; // S.No smaller

        // ✅ Use the same data as your UI
        if (!employeesNotRegistred || employeesNotRegistred.length === 0) {
            worksheet.addRow(["No employee data available"]);
        } else {
            employeesNotRegistred.forEach((row, index) => {
                worksheet.addRow([
                    index + 1,
                    row.uanNumber || "",
                    row.uanMemberId || "",
                    `${row.givenName || ""} ${row.surname || ""}`,
                    row.dateOfBirth || "",
                    row.dateOfJoining || "",
                    row.gender || "",
                    `${row.fatherGivenName || ""} ${row.fatherSurname || ""}`,
                    row.relation || "",
                    row.contactNumber || "",
                    row.email || "",
                    row.nationality || "",
                    row.epfWages || "",
                    row.qualification || "",
                    row.maritalStatus || "",
                    row.isInternationalWorker ? "Yes" : "No",
                    row.countryOfOrigin || "",
                    row.passportNumber || "",
                    row.passportValidFrom || "",
                    row.passportValidTo || "",
                    row.isPhysicallyHandicapped ? "Yes" : "No",
                    row.locomotive ? "Yes" : "No",
                    row.hearing ? "Yes" : "No",
                    row.visual ? "Yes" : "No",
                    row.bankAccountNumber || "",
                    row.ifscCode || "",
                    row.accountHolderName || "",
                    row.panCardNumber || "",
                    row.nameAsPerPanCard || "",
                    row.aadhaarNumber || "",
                    row.nameAsPerAadhaar || "",
                ]);
            });
        }

        // Align all data center-left for better readability
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > 2) {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
                });
            }
        });

        // Download Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `4Gid_PF_Reg.xlsx`;
        link.click();
    };




    // const generatePDF = () => {
    //     if (attendanceData.length === 0) {
    //         alert("No data to export");
    //         return;
    //     }

    //     // Use A4 Portrait
    //     const doc = new jsPDF({
    //         orientation: "portrait",
    //         unit: "mm",
    //         format: "a4",
    //     });
    //     const pageWidth = doc.internal.pageSize.getWidth();

    //     // Title centered
    //     doc.setFontSize(16);
    //     doc.setFont(undefined, "bold");
    //     doc.text("PF Registration Report", pageWidth / 2, 15, { align: "center" });

    //     // --- Letter Heading (static) ---
    //     doc.setFontSize(11);
    //     doc.setFont(undefined, "normal");
    //     doc.text("To,", 14, 30);
    //     doc.text("The Managing Director", 25, 38);
    //     doc.text("BSFC, Khadya Bhawan", 25, 46);
    //     doc.text("Patna, Bihar.", 25, 54);

    //     // Filters & Report Date
    //     const stateLabel = stateOptions.find(opt => opt.value === state)?.label || "N/A";
    //     const districtLabel = districtOptions.find(opt => opt.value === district)?.label || "N/A";
    //     const monthLabel = monthOptions.find(opt => opt.value === month)?.label || "N/A";

    //     const now = new Date();
    //     const dd = String(now.getDate()).padStart(2, "0");
    //     const mm = String(now.getMonth() + 1).padStart(2, "0");
    //     const yyyy = now.getFullYear();
    //     const reportDateTime = `${dd}-${mm}-${yyyy}`;

    //     doc.setFontSize(11);
    //     doc.text(`Report Date: ${reportDateTime}`, pageWidth - 14, 30, { align: "right" });
    //     // District (label normal, value bold)
    //     const districtY = 70;

    //     // First, measure "District: " width
    //     doc.setFont(undefined, "normal");
    //     const label = "District: ";
    //     const labelWidth = doc.getTextWidth(label);

    //     // Position so text ends at the right margin
    //     let x = pageWidth - 14 - (labelWidth + doc.getTextWidth(String(districtLabel)));

    //     // Draw label (normal)
    //     doc.text(label, x, districtY);

    //     // Draw value (bold, right after label)
    //     doc.setFont(undefined, "bold");
    //     doc.text(String(districtLabel), x + labelWidth, districtY);

    //     // Reset font for later
    //     doc.setFont(undefined, "normal");


    //     // Attendance record title with mixed styles
    //     let startX = 14;
    //     const y = 70;
    //     doc.setFontSize(11);

    //     // Static text (normal)
    //     doc.setFont(undefined, "normal");
    //     doc.text("Attendance Record for the Month of ", startX, y);

    //     // Move X forward
    //     startX += doc.getTextWidth("Attendance Record for the Month of ");

    //     // Month (bold)
    //     doc.setFont(undefined, "bold");
    //     doc.text(String(monthLabel), startX, y);
    //     startX += doc.getTextWidth(String(monthLabel));

    //     // Static " and Year " (normal)
    //     doc.setFont(undefined, "normal");
    //     doc.text(" and Year  ", startX, y);
    //     startX += doc.getTextWidth(" and Year  ");

    //     // Year (bold)
    //     doc.setFont(undefined, "bold");
    //     doc.text(String(year || "N/A"), startX, y);

    //     // Reset font
    //     doc.setFont(undefined, "normal");


    //     // --- Table starts below header (shift down because of letter heading) ---
    //     const tableColumns = [
    //         "S.No",
    //         "Employee Name",
    //         "Contact No.",
    //         "Designation",
    //         "Location",
    //         "No of Days in the Month",
    //         "No of Days Present",
    //         "Casual Leave (CL)",
    //         "No of Days Absent",
    //         "Total No of Days Present",
    //         "CL Used Status",
    //         "Total",
    //     ];

    //     const groupedData = attendanceData.reduce((acc, item) => {
    //         (acc[item.designation] = acc[item.designation] || []).push(item);
    //         return acc;
    //     }, {});

    //     const tableRows = [];
    //     let serial = 1;
    //     let grandTotal = 0;
    //     const groupRowRanges = [];

    //     for (const [designation, records] of Object.entries(groupedData)) {
    //         const groupTotal = records.reduce((sum, r) => sum + (r.totalNoOfDaysPresent || 0), 0);
    //         grandTotal += groupTotal;

    //         const startRow = tableRows.length;

    //         records.forEach((row, idx) => {
    //             tableRows.push([
    //                 serial++,
    //                 row.employeeName || "",
    //                 row.contactNo || "",
    //                 row.designation || "",
    //                 row.location || "",
    //                 row.totalDaysInMonth || 0,
    //                 row.presentDays || 0,
    //                 row.casualLeave || 0,
    //                 row.absentDays || 0,
    //                 row.totalNoOfDaysPresent || 0,
    //                 row.clUsedStatus || "",
    //                 idx === records.length - 1 ? groupTotal : "",
    //             ]);
    //         });

    //         const endRow = tableRows.length - 1;
    //         groupRowRanges.push({ start: startRow, end: endRow, total: groupTotal });
    //     }

    //     doc.autoTable({
    //         startY: 75,
    //         head: [tableColumns],
    //         body: tableRows,
    //         theme: "grid",
    //         styles: { fontSize: 8, cellPadding: 2 },
    //         headStyles: { fillColor: [68, 114, 196], textColor: 255, fontStyle: "bold" },
    //         columnStyles: {
    //             0: { halign: "center" },
    //             12: { halign: "right" },
    //         },
    //         margin: { left: 14, right: 14 },
    //     });

    //     // (keep your merged "Total" column logic here if needed...)

    //     let finalY = doc.lastAutoTable.finalY + 10;
    //     doc.setFont(undefined, "bold");
    //     doc.text(`Total  ${grandTotal}`, pageWidth - 14, finalY, { align: "right" });

    //     finalY += 15;

    //     // Signature section
    //     doc.setFontSize(11);
    //     doc.setFont(undefined, "normal");
    //     doc.text("Cc.", 15, finalY);
    //     doc.text("1. To 4G Identity Solutions Pvt. Ltd.", 25, finalY);

    //     doc.text("IT Manager 4G Identity Solutions", 90, finalY + 25, { align: "center" });
    //     doc.text("District Manager, BSFC", pageWidth - 40, finalY + 25, { align: "center" });

    //     doc.text("Signature", 90, finalY + 30, { align: "center" });
    //     doc.text("Signature", pageWidth - 40, finalY + 30, { align: "center" });

    //     doc.save(`Monthly_Attendance_${month}_${year}.pdf`);
    // };

    const generatePDF = () => {
        if (attendanceData.length === 0) {
            alert("No data to export");
            return;
        }

        // Use A4 Portrait
        const doc = new jsPDF({
            orientation: "portrait", //landscape
            unit: "mm",
            format: "a4",
        });
        const pageWidth = doc.internal.pageSize.getWidth();

        // Title centered
        doc.setFontSize(16);
        doc.setFont(undefined, "bold");
        doc.text("PF Registration Report", pageWidth / 2, 15, { align: "center" });

        // --- Letter Heading (static) ---
        doc.setFontSize(11);
        doc.setFont(undefined, "normal");
        doc.text("To,", 14, 30);
        doc.text("The Managing Director", 25, 38);
        doc.text("BSFC, Khadya Bhawan", 25, 46);
        doc.text("Patna, Bihar.", 25, 54);

        // Filters & Report Date
        const stateLabel = stateOptions.find(opt => opt.value === state)?.label || "N/A";
        const districtLabel = districtOptions.find(opt => opt.value === district)?.label || "N/A";
        const monthLabel = monthOptions.find(opt => opt.value === month)?.label || "N/A";
        const projectLabel = projectResponse.find((opt) => opt.value === project)?.label || "N/A";

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, "0");
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const yyyy = now.getFullYear();
        const reportDateTime = `${dd}-${mm}-${yyyy}`;

        doc.setFontSize(11);
        doc.text(`Report Date: ${reportDateTime}`, pageWidth - 14, 30, { align: "right" });


        // Project (label normal, value bold) → Right side
        const projectY = 65;
        const projectLabelText = "Project: ";
        const projectValueText = String(projectLabel);

        // total width of label + value
        const projectTextWidth =
            doc.getTextWidth(projectLabelText) + doc.getTextWidth(projectValueText);

        // starting x so that full text ends 14mm before right edge
        let projectX = pageWidth - 14 - projectTextWidth;

        // draw label (normal)
        doc.setFont(undefined, "normal");
        doc.text(projectLabelText, projectX, projectY);

        // draw value (bold)
        doc.setFont(undefined, "bold");
        doc.text(projectValueText, projectX + doc.getTextWidth(projectLabelText), projectY);


        // District (label normal, value bold)
        const districtY = 70;
        const label = "District: ";
        doc.setFont(undefined, "normal");
        const labelWidth = doc.getTextWidth(label);
        let x = pageWidth - 14 - (labelWidth + doc.getTextWidth(String(districtLabel)));
        doc.text(label, x, districtY);
        doc.setFont(undefined, "bold");
        doc.text(String(districtLabel), x + labelWidth, districtY);
        doc.setFont(undefined, "normal");

        // Attendance record title with mixed styles
        let startX = 14;
        const y = 70;
        doc.setFontSize(11);

        doc.setFont(undefined, "normal");
        doc.text("Attendance Record for the Month of ", startX, y);
        startX += doc.getTextWidth("Attendance Record for the Month of ");

        doc.setFont(undefined, "bold");
        doc.text(String(monthLabel), startX, y);
        startX += doc.getTextWidth(String(monthLabel));

        doc.setFont(undefined, "normal");
        doc.text(" and Year ", startX, y);
        startX += doc.getTextWidth(" and Year ");

        doc.setFont(undefined, "bold");
        doc.text(String(year || "N/A"), startX, y);
        doc.setFont(undefined, "normal");

        // --- Table starts below header ---
        const tableColumns = [
            "S.No",
            "Employee Name",
            "Contact Number",
            "Designation",
            "DHQ/ Godown Name",
            "No of Days in the Month",
            "No of Days Present",
            "Casual Leave (CL)",
            "No of Days Absent",
            "Total No of Days Present",
            "CL Used Status",
            "Total",
        ];

        const groupedData = attendanceData.reduce((acc, item) => {
            (acc[item.designation] = acc[item.designation] || []).push(item);
            return acc;
        }, {});

        const tableRows = [];
        let serial = 1;
        let grandTotal = 0;
        const groupRowRanges = [];

        for (const [designation, records] of Object.entries(groupedData)) {
            const groupTotal = records.reduce((sum, r) => sum + (r.totalNoOfDaysPresent || 0), 0);
            grandTotal += groupTotal;

            const startRow = tableRows.length;

            records.forEach((row, idx) => {
                tableRows.push([
                    serial++,
                    row.employeeName || "",
                    row.contactNo || "",
                    row.designation || "",
                    row.location || "",
                    row.totalDaysInMonth || 0,
                    row.presentDays || 0,
                    row.casualLeave || 0,
                    row.absentDays || 0,
                    row.totalNoOfDaysPresent || 0,
                    row.clUsedStatus || "",
                    idx === records.length - 1 ? groupTotal : "", // Only last row shows total
                ]);
            });

            const endRow = tableRows.length - 1;
            groupRowRanges.push({ start: startRow, end: endRow, total: groupTotal });
        }

        doc.autoTable({
            startY: 75,
            head: [tableColumns],
            body: tableRows,
            theme: "grid",
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [68, 114, 196], textColor: 255, fontStyle: "bold" },
            columnStyles: {
                0: { halign: "center" },
                12: { halign: "right" },
            },
            margin: { left: 14, right: 14 },
        });

        // --- Merge "Total" column per designation ---
        const table = doc.lastAutoTable;
        const colIndex = 12; // "Total" column index

        groupRowRanges.forEach(({ start, end, total }) => {
            if (end >= start) {
                const firstRow = table.body[start];
                const lastRow = table.body[end];

                if (!firstRow || !lastRow) return;

                const yStart = firstRow.y;
                const yEnd = lastRow.y + lastRow.height;

                const col = table.columns[colIndex];
                if (!col) return;

                const x = col.startX;
                const width = col.width;
                const height = yEnd - yStart;

                doc.setDrawColor(0);
                doc.setFillColor(230);
                doc.rect(x, yStart, width, height, "F");

                doc.setFont(undefined, "bold");
                doc.setFontSize(8);
                doc.setTextColor(0);

                const textX = x + width / 2;
                const textY = yStart + height / 2;

                doc.text(String(total), textX, textY, { align: "center", baseline: "middle" });
            }
        });


        // --- Grand total row ---
        // --- Grand total row ---
        let finalY = doc.lastAutoTable.finalY;
        doc.autoTable({
            body: [
                [" ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", "Total", " ", grandTotal]
            ],
            theme: "grid",
            styles: { fontSize: 9, halign: "center", textColor: [0, 0, 0] },
            columnStyles: {
                9: { fontStyle: "bold", halign: "right", textColor: [0, 0, 0] },  // "Total" under col 9 header
                11: { fontStyle: "bold", halign: "center", textColor: [0, 0, 0] } // Grand total value under col 11
            },
            startY: finalY,
            margin: { left: 14, right: 14 },
        });


        finalY = doc.lastAutoTable.finalY + 15;

        // Signature section
        doc.setFontSize(11);
        doc.setFont(undefined, "normal");
        doc.text("Cc.", 15, finalY);
        doc.text("1. To 4G Identity Solutions Pvt. Ltd.", 25, finalY);

        doc.text("IT Manager 4G Identity Solutions", 90, finalY + 10, { align: "center" });
        doc.text("District Manager, BSFC", pageWidth - 40, finalY + 10, { align: "center" });

        doc.text("Signature", 90, finalY + 15, { align: "center" });
        doc.text("Signature", pageWidth - 40, finalY + 15, { align: "center" });

        doc.save(`Monthly_Attendance_${month}_${year}.pdf`);
    };



    // const groupedEmployees = attendanceData.reduce((acc, employee) => {
    //     const { designation } = employee;
    //     if (!acc[designation]) {
    //         acc[designation] = [];
    //     }
    //     acc[designation].push(employee);
    //     return acc;
    // }, {});

    // const designationTotals = designationData.reduce((acc, item) => {
    //     acc[item.designation.trim().toLowerCase()] = item.totalPresentDays;
    //     return acc;
    // }, {});


    return (
        <div className="PFRegistration-report-container">
            <div className="PFRegistration-report-box">
                <h2 className="PFRegistration-report-heading">PF Registration Report</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}
                <form onSubmit={handleSubmit}
                >
                    <div className="PFRegistration-report-maincontainer">
                        <div className="PFRegistration-report-container-part">
                            <div className={`PFRegistration-report-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}

                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="PFRegistration-report-container-part">
                            <div className={`PFRegistration-report-floating-label-container ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}

                                />
                                <label>District</label>
                                {errors.district && <div className="error-message">{errors.district}</div>}
                            </div>
                        </div>
                    </div>
                    {/* <div className="PFRegistration-report-maincontainer">
                        <div className="PFRegistration-report-container-part">
                            <div className={`PFRegistration-report-floating-label-container ${isFocused.year || year ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={year ? new Date(year, 0, 1) : null}
                                    onChange={handleYearChange}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, year: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, year: false }))}
                                    dateFormat="yyyy"
                                    showYearPicker
                                    placeholderText=""
                                    className="PFRegistration-report-date-picker"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    ref={yearInputRef}
                                  
                                    title="Select a year" 
                                />
                                <label><span className="required">*</span>Year</label>
                                {errors.year && <div className="error-message">{errors.year}</div>}
                            </div>
                        </div>
                        <div className="PFRegistration-report-container-part">
                            <div className={`PFRegistration-report-floating-label-container ${isFocused.month || month ? 'float-label' : ''}`}>
                                <Select
                                    name="month"
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === month) || null}
                                    onChange={option => handleInputChange(option, 'month')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, month: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, month: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Month</label>
                                {errors.month && <div className="error-message">{errors.month}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="PFRegistration-report-maincontainer">

                        <div className="PFRegistration-report-container-part">
                            <div className={`PFRegistration-report-floating-label-container ${isFocused.project || project ? 'float-label' : ''}`}>
                                <Select
                                    name="project"
                                    options={projectResponse}
                                    value={projectResponse.find(opt => opt.value === project) || null}
                                    onChange={option => handleInputChange(option, 'project')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, project: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, project: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>
                                    <span className="required">*</span>Project
                                </label>
                                {errors.project && <div className="error-message">{errors.project}</div>}
                            </div>
                        </div>
                        <div className="PFRegistration-report-container-part">

                        </div>
                    </div> */}
                    <div className="PFRegistration-report-btn-div">
                        <button type="submit" className="PFRegistration-report-btn"
                            disabled={!isFetchDataEnabled}


                        >
                            Generate Report
                        </button>
                    </div>
                </form>
            </div>

            <div className="PFRegistration-report-btn-div">
                <button
                    type="button"
                    className="PFRegistration-report-btn"
                    onClick={generateExcel}
                    disabled={employeesNotRegistred.length === 0}  // ✅ disabled if no data
                >
                    Download Excel
                </button>
                {/* <button
                        type="button"
                        className="PFRegistration-report-btn"
                        onClick={generatePDF}
                        disabled={attendanceData.length === 0}  // ✅ disabled if no data
                    >
                        Download PDF
                    </button> */}
            </div>
            <div className="PFRegistration-table-container">



                <table className="PFRegistration-table">
                    <div className='pf-register-head-body'>
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>UAN</th>
                                <th>Prv MemberId</th>
                                <th>Member Name</th>
                                <th >Date of Birth</th>
                                <th>Date of Joining</th>
                                <th>Gender</th>
                                <th>Father's Name</th>
                                <th>Relation</th>
                                <th>Mobile Number</th>
                                <th>Email</th>
                                <th>Nationality</th>
                                <th>EPF Wages</th>
                                <th>Qualification</th>
                                <th>Marital Status</th>
                                <th>Is International Worker</th>
                                <th>Country of Origin</th>
                                <th>Passport Number</th>
                                <th>Passport Valid From</th>
                                <th>Passport Valid Till</th>
                                <th>Is Phisical Handicap</th>
                                <th>Iocomotive</th>
                                <th>Hearing</th>
                                <th>Visual</th>
                                <th>Bank Account No</th>
                                <th>IFSC Code</th>
                                <th>Name As Per Bank</th>
                                <th>PAN</th>
                                <th>Name As Per Pan</th>
                                <th>Aadhar No</th>
                                <th>Name As Per Aadhar</th>

                            </tr>
                        </thead>
                        <tbody>
                            {employeesNotRegistred.length > 0 ? (
                                employeesNotRegistred.map((row, index) => (
                                    <tr key={row.employeeId}>
                                        <td>{index + 1}</td>
                                        <td>{row.uanNumber}</td>
                                        <td>{row.uanMemberId}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.givenName} {row.surname}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.dateOfBirth ? new Date(row.dateOfBirth).toLocaleDateString('en-GB').replace(/\//g, '-') : '-'}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.dateOfJoining ? new Date(row.dateOfJoining).toLocaleDateString('en-GB').replace(/\//g, '-') : '-'}</td>

                                        <td>{row.gender}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.fatherGivenName} {row.fatherSurname}</td>
                                        <td>{row.relation}</td>
                                        <td>{row.contactNumber}</td>
                                        <td>{row.email}</td>
                                        <td>{row.nationality}</td>
                                        <td>{row.epfWages}</td>
                                        <td>{row.qualification}</td>
                                        <td>{row.maritalStatus}</td>
                                        <td>{row.isInternationalWorker ? 'Yes' : 'No'}</td>
                                        <td>{row.countryOfOrigin || '-'}</td>
                                        <td>{row.passportNumber || '-'}</td>
                                        <td>{row.passportValidFrom || '-'}</td>
                                        <td>{row.passportValidTo || '-'}</td>
                                        <td>{row.isPhysicallyHandicapped ? 'Yes' : 'No'}</td>
                                        <td>{row.locomotive ? 'Yes' : 'No'}</td>
                                        <td>{row.hearing ? 'Yes' : 'No'}</td>
                                        <td>{row.visual ? 'Yes' : 'No'}</td>
                                        <td>{row.bankAccountNumber}</td>
                                        <td>{row.ifscCode}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.accountHolderName}</td>
                                        <td>{row.panCardNumber}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.nameAsPerPanCard}</td>
                                        <td>{row.aadhaarNumber}</td>
                                        <td style={{whiteSpace:'nowrap'}}>{row.nameAsPerAadhaar}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="31" style={{ textAlign: "center" }}>
                                        No employee data available.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </div>

                </table>
            </div>


        </div>
    );
};

export default PfRegistration;